import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { Outlet, useLocation, useNavigation } from 'react-router'

import { checkOnRouteChange } from '@/app/version-check-route'
import { PageLoader } from '@/components/primitives/page-loader'
import { MainHeader } from '@/components/main-header'
import { AraSessionsProvider } from '@/features/ara-generation/providers/ara-sessions-provider'

/**
 * App shell for every route. Renders the global Scope header above the matched
 * route via `<Outlet />`, plus a slim top progress bar while the router is
 * transitioning (lazy chunk fetch or loaders). The bar is non-blocking, so the
 * current page stays visible during client-side navigation instead of blanking
 * out. The flex-column layout keeps the header pinned without overlaying
 * content or requiring artificial top padding.
 */
export function RootLayout() {
  const navigation = useNavigation()
  const isNavigating = navigation.state !== 'idle'
  const queryClient = useQueryClient()
  const location = useLocation()
  const pathnameRef = useRef(location.pathname)

  useEffect(() => {
    if (pathnameRef.current === location.pathname) return
    pathnameRef.current = location.pathname
    checkOnRouteChange(queryClient)
  }, [location.pathname, queryClient])

  return (
    <div className="relative flex h-screen flex-col bg-gray-50">
      <MainHeader />
      {isNavigating && (
        <div
          role="progressbar"
          aria-label="Loading page"
          data-testid="route-progress"
          className="pointer-events-none fixed inset-x-0 top-0 z-50 h-0.5 overflow-hidden bg-primary/20"
        >
          <div className="route-progress-bar h-full w-1/3 bg-primary" />
        </div>
      )}
      <main className="min-h-0 flex-1 overflow-auto">
        <AraSessionsProvider>
          <Outlet />
        </AraSessionsProvider>
      </main>
    </div>
  )
}

/**
 * Full-screen fallback shown on the very first paint (hard load / refresh) while
 * the initial route chunk resolves — replaces the blank page. Client-side
 * navigations use the non-blocking bar above instead.
 */
export function RootHydrateFallback() {
  return (
    <div className="h-full">
      <PageLoader />
    </div>
  )
}
